import React, { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import KBList, { KBButton } from '../KBList'
import {
  type ChatMessage,
  useConversationContext,
  type BaseMessage,
} from '../../../../stores/conversation.store'
import BasePage from '../../../components/BasePage'
import { getChat } from '../../../../api/chat'
import {
  mapStoredMessagesToUIMessages,
  scrollToBottom,
} from '../../../../utils'
import ResponseBubble from '../../../components/ResponseBubble'
import RequestBubble from '../../../components/RequestBubble'
import { useStyles } from '../../style'
import ChatInput from '../../../ChatPage/components/input'

import { useModelConfigContext } from '../../../../stores/modelConfig.store'
import ModelSelect from '@/pages/components/ModelSelect'
interface RagConversationViewProps {
  conversationId: string
}

interface RagUiMessage extends BaseMessage {
  role: 'USER' | 'ASSISTANT'
  content: string
  createTime: string
  messageId: string
  isError: boolean
}

const RagConversationView = ({ conversationId }: RagConversationViewProps) => {
  const { styles } = useStyles()
  const location = useLocation()
  const [inputContent, setInputContent] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [historyMessages, setHistoryMessages] = useState<any[]>([])
  const msgContainerRef = useRef<HTMLDivElement>(null)
  const {
    activeConversation,
    aiCapabilities,
    chooseActiveConversation,
    processSendMessage,
    appendAssistantMessage,
    deleteMessageAndAfter,
    updateMessageContent,
    processReloadMessage,
    updateActiveConversation,
    fetchConversations,
  } = useConversationContext()
  const { currentModel } = useModelConfigContext()
  const isFirstLoad = useRef(true)
  const processedPrompts = useRef(new Set<string>())
  const [selectedRag, setSelectedRag] = React.useState<string>('')
  const [isSmallScreen, setIsSmallScreen] = useState(false)
  useEffect(() => {
    chooseActiveConversation(conversationId)
  }, [conversationId])

  useEffect(() => {
    const checkScreenSize = () => {
      setIsSmallScreen(window.innerWidth < 1080)
    }

    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)

    return () => {
      window.removeEventListener('resize', checkScreenSize)
    }
  }, [])

  useEffect(() => {
    scrollToBottom(msgContainerRef.current)
  }, [historyMessages])

  useEffect(() => {
    if (!activeConversation) {
      setHistoryMessages([])
      return
    }
    const messages = activeConversation.messages ?? []

    const filteredMessages = messages.filter((msg: ChatMessage) => !msg.isError)
    setHistoryMessages(mapStoredMessagesToUIMessages(filteredMessages))
  }, [activeConversation?.messages])

  useEffect(() => {
    if (isFirstLoad.current && activeConversation) {
      const queryParams = new URLSearchParams(location.search)
      const urlPrompt = queryParams.get('prompt')
      const urlImageUrls = queryParams.get('imageUrls')
      const urlDocUrls = queryParams.get('docUrls')

      let imageUrls: string[] = []
      let docUrls: any[] = []
      try {
        if (urlImageUrls) {
          imageUrls = JSON.parse(urlImageUrls)
        }
        if (urlDocUrls) {
          docUrls = JSON.parse(urlDocUrls)
        }
      } catch (error) {
        console.warn('解析URL参数失败:', error)
      }

      if (urlPrompt && !processedPrompts.current.has(urlPrompt)) {
        processedPrompts.current.add(urlPrompt)
        const newUrl = window.location.hash.split('?')[0]
        window.history.replaceState({}, document.title, newUrl)
        handleSendMessage(
          urlPrompt, imageUrls, docUrls,
          activeConversation.extConfig?.ragTag || 'HOPEInverter'
        )
        const timeId = setTimeout(() => {
          fetchConversations()
          clearTimeout(timeId)
        }, 3000)
      }
      if (!selectedRag)
        setSelectedRag(activeConversation.extConfig?.ragTag || 'HOPEInverter')
      isFirstLoad.current = false
    }
  }, [location.search, activeConversation])

  const updateConversationMessages = (
    assistantContent: any,
    role: 'ASSISTANT',
    isError: boolean = false,
    userTimestamp: string,
    userMessage: RagUiMessage,
    finalMessages: RagUiMessage[]
  ) => {
    appendAssistantMessage(
      assistantContent,
      role,
      isError,
      userTimestamp,
      userMessage,
      finalMessages as RagUiMessage[]
    )
  }

  const handleSendMessage = async (
    text: string
    , imageUrls: string[], docUrls: any[],
    ragTag: string = selectedRag
  ) => {
    const createMessage = (text: string, timestamp: string): RagUiMessage => ({
      role: 'USER',
      content: text,
      isError: false,
      createTime: timestamp,
      messageId: timestamp,
      medium: {
        images: imageUrls.map((url) => ({ url })),
        docs: docUrls
      }
    })

    const sendRequest = async (
      text: string,
      userTimestamp: string,
      userMessage: RagUiMessage,
      finalMessages: RagUiMessage[]
    ) => {
      await getChat(
        (data: any) => {
          updateConversationMessages(
            data,
            'ASSISTANT',
            false,
            userTimestamp,
            userMessage,
            finalMessages
          )
        },
        {
          conversationId: activeConversation?.conversationId,
          model: currentModel || 'dashscope',
          prompt: text,
          ragTag: ragTag,
          conversationType: 'rag_chat',
          enableSearch: aiCapabilities.onlineSearch,
          enableThinking: aiCapabilities.deepThink,
          enableRag: true,
          enableExpandQuery: false,
          enableFunctionCalling: false,
          enableMcp: false,
          imageUrls,
          docUrls
        }
      )
    }

    await processSendMessage({
      text,
      imageUrls,
      docUrls,
      sendRequest,
      createMessage,
      setLoading: setIsLoading,
      setInputContent,
    })
  }

  // 处理重新生成消息（revert操作）
  const handleReloadMessage = async (
    messageId: string,
    imageUrls: string[], docUrls: any[],
    ragTag: string = selectedRag
  ) => {
    if (!activeConversation || isLoading) return

    const sendRequest = async (
      text: string,
      messageId: string,
      userMessage: RagUiMessage,
      finalMessages: RagUiMessage[]
    ) => {
      await getChat(
        (data: any) => {
          updateConversationMessages(
            data,
            'ASSISTANT',
            false,
            messageId,
            userMessage,
            finalMessages
          )
        },
        {
          conversationId: activeConversation?.conversationId,
          model: currentModel || 'dashscope',
          prompt: text,
          ragTag: ragTag,
          conversationType: 'rag_chat',
          enableSearch: aiCapabilities.onlineSearch,
          enableThinking: aiCapabilities.deepThink,
          enableRag: true,
          enableExpandQuery: false,
          enableFunctionCalling: false,
          enableMcp: false,
          imageUrls,
          docUrls
        }
      )
    }

    await processReloadMessage({
      messageId,
      sendRequest,
      setLoading: setIsLoading,
      setInputContent,
    })
  }

  // 处理编辑消息
  // const handleEditConfirm = async (
  //   messageTimestamp: number,
  //   newContent: string
  // ) => {
  //   if (!activeConversation || isLoading) return

  //   // 找到要编辑的消息
  //   const message = activeConversation.messages.find(
  //     (msg) => msg.timestamp === messageTimestamp
  //   )

  //   if (!message || message.role !== 'USER') return

  //   // 删除当前消息之后的所有消息（保留用户消息本身）
  //   const remainingMessages = activeConversation.messages.filter(
  //     (msg) => msg.timestamp <= messageTimestamp
  //   )

  //   // 更新用户消息内容
  //   const updatedMessages = remainingMessages.map((msg) =>
  //     msg.timestamp === messageTimestamp
  //       ? ({ ...msg, content: newContent } as RagUiMessage)
  //       : msg
  //   ) as RagUiMessage[]

  //   // 立即更新会话状态
  //   updateActiveConversation({
  //     ...activeConversation,
  //     messages: updatedMessages,
  //   })

  //   // 直接重新生成回复
  //   setIsLoading(true)

  //   // 创建一个使用正确baseMessages的更新函数
  //   const updateConversationMessagesWithBase = (
  //     messageContent: string,
  //     role: 'assistant',
  //     isError: boolean = false,
  //     userTimestamp: number,
  //     userMessage: RagUiMessage
  //   ) => {
  //     appendAssistantMessage(
  //       messageContent,
  //       role,
  //       isError,
  //       userTimestamp,
  //       userMessage,
  //       updatedMessages
  //     )
  //   }

  //   // 创建更新后的用户消息
  //   const updatedUserMessage: RagUiMessage = {
  //     ...message,
  //     content: newContent,
  //   } as RagUiMessage

  //   const sendRequest = async (
  //     text: string,
  //     userTimestamp: number,
  //     userMessage: RagUiMessage
  //   ) => {
  //     let accumulatedText = ''
  //     const response = await getRag(
  //       text,
  //       (value) => {
  //         const chunk = new TextDecoder().decode(value)
  //         accumulatedText += chunk
  //         updateConversationMessagesWithBase(
  //           accumulatedText,
  //           'assistant',
  //           false,
  //           userTimestamp,
  //           userMessage
  //         )
  //       },
  //       { chatId: conversationId }
  //     )

  //     if (!response.ok) {
  //       throw new Error('RAG查询失败')
  //     }
  //   }

  //   try {
  //     await sendRequest(newContent, message.timestamp, updatedUserMessage)
  //   } catch (error) {
  //     console.error('重新生成消息错误:', error)
  //     appendAssistantMessage(
  //       '抱歉，重新生成回复时出现错误。',
  //       'ASSISTANT',
  //       true,
  //       message.timestamp,
  //       updatedUserMessage,
  //       updatedMessages
  //     )
  //   } finally {
  //     setIsLoading(false)
  //   }
  // }

  return (
    <BasePage title="RAG对话" conversationId={conversationId}>
      <div className={styles.container}>
        {!isSmallScreen && (
          <div className={styles.leftPanel}>
            <KBList selectedRag={selectedRag} setSelectedRag={setSelectedRag} />
          </div>
        )}

        <div
          className={styles.rightPanel}
          style={isSmallScreen ? { width: '100%' } : {}}
        >
          {isSmallScreen && (
            <header className={styles.landingHeader}>
              <ModelSelect />
            </header>
          )}
          <div className={`${styles.card} ${styles.resultPanel}`}>
            <div className={styles.messagesContainer}>
              <div className={styles.article} ref={msgContainerRef}>
                {historyMessages.map((message) =>
                  message.sender === 'USER' ? (
                    <RequestBubble
                      key={message.id}
                      content={message.text}
                      images={message.images} docs={message.docs}
                    // onEditConfirm={(newContent) =>
                    //   handleEditConfirm(message.timestamp, newContent)
                    // }
                    />
                  ) : (
                    <ResponseBubble
                      key={message.id}
                      content={message.text}
                      thinkContent={message.thinkContent}
                      onlineSearchResults={message.onlineSearchResults}
                      isFinished={message.isFinished}
                      isError={message.isError}
                      onReload={() => handleReloadMessage(message.id, message.images, message.docs)}
                    />
                  )
                )}
              </div>
            </div>

            {isSmallScreen && (
              <KBButton
                selectedRag={selectedRag}
                setSelectedRag={setSelectedRag}
              />
            )}
            <ChatInput
              inputValue={inputContent}
              onInputChange={setInputContent}
              onSubmit={handleSendMessage}
              isLoading={isLoading}
              placeholder="您可以通过点击左侧变频器提问了解森兰知识库"
              isActionButton={true}
            />
          </div>
        </div>
      </div>
    </BasePage>
  )
}

export default RagConversationView
